import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Pencil, Plus, Trash2, Loader2, Building2 } from 'lucide-react';
import RichTextEditor from '@/components/ui/rich-text-editor';
import RichTextDisplay from '@/components/ui/rich-text-display';
import { CompanyHoverCard } from '@/components/company';

interface Experience {
  id: string;
  job_title: string;
  description?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  is_current?: boolean | null;
  products_worked?: string[] | null;
  company_id?: string | null;
  company_name?: string | null; // Fallback when company linking fails
  companies?: { name: string } | null;
}

interface Company {
  id: string;
  name: string;
}

interface EditableExperienceProps {
  candidateId: string;
  experiences: Experience[];
  onUpdate: () => void;
}

const EditableExperience = ({ candidateId, experiences, onUpdate }: EditableExperienceProps) => {
  const { toast } = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExp, setSelectedExp] = useState<Experience | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    job_title: '',
    company_id: '',
    description: '',
    start_date: '',
    end_date: '',
    is_current: false,
    products_worked: '',
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    const { data } = await supabase.from('companies').select('id, name').order('name');
    if (data) setCompanies(data);
  };

  const openAdd = () => {
    setSelectedExp(null);
    setFormData({
      job_title: '',
      company_id: '',
      description: '',
      start_date: '',
      end_date: '',
      is_current: false,
      products_worked: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (exp: Experience) => {
    setSelectedExp(exp);
    setFormData({
      job_title: exp.job_title || '',
      company_id: exp.company_id || '',
      description: exp.description || '',
      start_date: exp.start_date || '',
      end_date: exp.end_date || '',
      is_current: exp.is_current || false,
      products_worked: exp.products_worked?.join(', ') || '',
    });
    setDialogOpen(true);
  };

  const openDelete = (exp: Experience) => {
    setSelectedExp(exp);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.job_title) {
      toast({ title: 'Job title is required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        job_title: formData.job_title,
        company_id: formData.company_id || null,
        description: formData.description || null,
        start_date: formData.start_date || null,
        end_date: formData.is_current ? null : (formData.end_date || null),
        is_current: formData.is_current,
        products_worked: formData.products_worked ? formData.products_worked.split(',').map(p => p.trim()).filter(Boolean) : null,
        candidate_id: candidateId,
      };

      if (selectedExp) {
        const { error } = await supabase
          .from('candidate_experience')
          .update(payload)
          .eq('id', selectedExp.id);
        if (error) throw error;
        toast({ title: 'Experience updated' });
      } else {
        const { error } = await supabase
          .from('candidate_experience')
          .insert(payload);
        if (error) throw error;
        toast({ title: 'Experience added' });
      }
      setDialogOpen(false);
      onUpdate();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedExp) return;
    
    try {
      const { error } = await supabase
        .from('candidate_experience')
        .delete()
        .eq('id', selectedExp.id);
      if (error) throw error;
      toast({ title: 'Experience deleted' });
      setDeleteDialogOpen(false);
      onUpdate();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={openAdd}>
          <Plus className="h-4 w-4 mr-1" />
          Add Experience
        </Button>
      </div>

      {experiences.length > 0 ? (
        experiences.map((exp) => (
          <Card key={exp.id} className="p-4">
            <div className="flex justify-between items-start">
              <div className="flex gap-3">
                <Building2 className="h-5 w-5 text-muted-foreground mt-1" />
                <div>
                  <h4 className="font-semibold">{exp.job_title}</h4>
                  <CompanyHoverCard 
                    companyId={exp.company_id} 
                    companyName={exp.companies?.name || exp.company_name}
                    experienceId={exp.id}
                    className="text-sm"
                    onCompanyLinked={() => onUpdate()}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {exp.start_date} - {exp.end_date || 'Present'}
                    {exp.is_current && <Badge variant="outline" className="ml-2">Current</Badge>}
                  </p>
                  {exp.description && (
                    <RichTextDisplay content={exp.description} className="mt-2" />
                  )}
                  {exp.products_worked && exp.products_worked.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {exp.products_worked.map((product, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{product}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(exp)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => openDelete(exp)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          </Card>
        ))
      ) : (
        <p className="text-center text-muted-foreground py-8">No experience records</p>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedExp ? 'Edit Experience' : 'Add Experience'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Job Title *</Label>
              <Input 
                value={formData.job_title}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
              />
            </div>
            <div>
              <Label>Company</Label>
              <Select 
                value={formData.company_id} 
                onValueChange={(v) => setFormData({ ...formData, company_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <RichTextEditor 
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                placeholder="Describe responsibilities, achievements..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input 
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input 
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  disabled={formData.is_current}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="is_current"
                checked={formData.is_current}
                onCheckedChange={(c) => setFormData({ ...formData, is_current: !!c })}
              />
              <Label htmlFor="is_current">Currently working here</Label>
            </div>
            <div>
              <Label>Products Worked (comma-separated)</Label>
              <Input 
                value={formData.products_worked}
                onChange={(e) => setFormData({ ...formData, products_worked: e.target.value })}
                placeholder="Product A, Product B"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Experience</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this experience record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EditableExperience;
